import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../constants/accessControl';
import { hasPermission } from '../../utils/permissions';
import { financeService } from '../../services/financeService';
import {
  computeEntityFinancialTotals,
  formatFinanceCurrency,
  formatFinanceDate,
  getMembershipTypeLabel,
  getPaymentStatusLabel,
  getRecordDisplayDate,
  paymentStatusTone,
} from '../../utils/entityFinancialSummary';

const EntityFinancialRecordsPanel = ({
  entityType,
  entityId,
  entityName,
  region,
  regionOnly = false,
  title = 'License & Membership Financial Records',
  className = '',
  compact = false,
}) => {
  const { userProfile } = useAuth();
  const canViewFinance = hasPermission(userProfile, PERMISSIONS.FINANCE_MEMBERSHIPS)
    || hasPermission(userProfile, PERMISSIONS.FINANCE_DASHBOARD);

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadRecords = useCallback(async () => {
    if (!canViewFinance) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await financeService.getEntityFinancialRecords({
        entityType,
        entityId,
        entityName,
        region,
        regionOnly,
      });
      setRecords(data);
    } catch (err) {
      setError(err.message || 'Failed to load financial records');
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [canViewFinance, entityType, entityId, entityName, region, regionOnly]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  if (!canViewFinance) return null;

  const totals = computeEntityFinancialTotals(records);

  return (
    <div className={`bg-card border border-border rounded-lg card-shadow ${compact ? 'p-4' : 'p-6'} ${className}`}>
      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${compact ? 'mb-3' : 'mb-4'}`}>
        <div className="flex items-center gap-2">
          <Icon name="Receipt" size={compact ? 18 : 20} className="text-primary shrink-0" />
          <div>
            <h3 className={`font-semibold text-foreground ${compact ? 'text-base' : 'text-lg'}`}>{title}</h3>
            <p className="text-xs text-muted-foreground">
              License fees, membership payments, and payment status by period
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" iconName="RefreshCw" onClick={loadRecords} disabled={isLoading}>
            Refresh
          </Button>
          <Button variant="outline" size="sm" iconName="Plus" asChild>
            <Link to="/membership-records">Add Record</Link>
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
          <Icon name="Loader2" size={20} className="animate-spin text-primary" />
          Loading financial records...
        </div>
      ) : records.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
          No license or membership payment records found for this {regionOnly ? 'region' : 'entity'} yet.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 pr-3">Period</th>
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Payment Type</th>
                  <th className="py-2 pr-3">Purpose</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => {
                  const isPaid = record.paymentStatus === 'paid';
                  return (
                    <tr key={record.id} className="border-b border-border/60 last:border-0">
                      <td className="py-2.5 pr-3 font-medium text-foreground">
                        {record.seasonYear || '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-foreground">
                        {formatFinanceDate(getRecordDisplayDate(record))}
                      </td>
                      <td className="py-2.5 pr-3 text-foreground capitalize">
                        {record.paymentMethod || '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-foreground">
                        {getMembershipTypeLabel(record.membershipType)}
                      </td>
                      <td className={`py-2.5 pr-3 font-medium capitalize ${paymentStatusTone(record.paymentStatus)}`}>
                        {getPaymentStatusLabel(record.paymentStatus)}
                      </td>
                      <td className={`py-2.5 pr-3 text-right font-semibold ${isPaid ? 'text-success' : 'text-foreground'}`}>
                        {formatFinanceCurrency(record.amount, record.currency)}
                        <span className="block text-[10px] font-normal text-muted-foreground">
                          {isPaid ? 'Paid' : 'To be paid'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={`mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 ${compact ? 'text-sm' : ''}`}>
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              <p className="text-xs text-muted-foreground">Total Paid</p>
              <p className="text-lg font-bold text-success">{formatFinanceCurrency(totals.totalPaid)}</p>
            </div>
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              <p className="text-xs text-muted-foreground">Outstanding</p>
              <p className="text-lg font-bold text-warning">{formatFinanceCurrency(totals.totalOutstanding)}</p>
            </div>
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
              <p className="text-xs text-muted-foreground">Total To Be Paid</p>
              <p className="text-lg font-bold text-primary">{formatFinanceCurrency(totals.totalToBePaid)}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EntityFinancialRecordsPanel;
